import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/shared/api'
import type { LeadsViewResponse, LeadBatchChange } from '@intone/shared/types'

interface LeadsQueryParams {
  list_id: number
  order_by?: 'customer_number' | 'status' | 'last_call_ts'
  order_type?: 'asc' | 'desc'
  page_id?: number
  page_size?: number
  disposition?: string
  search?: string
}

/**
 * Hook to fetch leads for a list
 */
export function useLeads(params: LeadsQueryParams | null) {
  const apiClient = useApiClient();

  const queryString = params
    ? '?' +
      new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)])
      ).toString()
    : ''

  return useQuery({
    queryKey: ['leads', params],
    queryFn: async () => {
      return apiClient<LeadsViewResponse>(`/leads${queryString}`)
    },
    enabled: params !== null && params.list_id !== undefined,
  })
}

interface LeadExportItem {
  phone_number: string
  status: string
  data: Record<string, unknown>
  last_call_ts: string | null
}

export function useExportLeads() {
  const apiClient = useApiClient();

  return async (listId: number): Promise<LeadExportItem[]> => {
    const result = await apiClient<{ leads: LeadExportItem[] }>(
      `/leads/export?list_id=${listId}`
    )
    return result.leads
  }
}

interface LeadByPhoneResponse {
  id: string
  phone_number: string | null
  status: string | null
  data: Record<string, unknown>
  list_id: number
  list_name: string | null
  campaign_id: number | null
  campaign_name: string | null
  country_iso: string | null
  country_state: string | null
  timezone: string | null
  last_call_ts: string | null
  last_call_id: string | null
}

/**
 * Hook to fetch lead by phone number
 */
export function useLeadByPhone(phoneNumber: string | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['leads', 'by-phone', phoneNumber],
    queryFn: async () => {
      return apiClient<LeadByPhoneResponse>(`/leads/by-phone?phone_number=${phoneNumber}`)
    },
    enabled: phoneNumber !== null && phoneNumber.length > 0,
  })
}

/**
 * Hook to update lead status
 */
export function useUpdateLeadStatus() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leadId, status }: { leadId: string; status: string }) => {
      return apiClient<{ message: string }>(`/leads/${leadId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

/**
 * Hook to batch update leads
 */
export function useBatchUpdateLeads() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ list_id, changes }: { list_id: number; changes: LeadBatchChange[] }) => {
      return apiClient<{ updated: number }>('/leads/batch', {
        method: 'POST',
        body: JSON.stringify({ list_id, changes }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

/**
 * Hook to delete a lead
 */
export function useDeleteLead() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (leadId: string) => {
      return apiClient<{ message: string }>(`/leads/${leadId}`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

/**
 * Hook to upsert a lead
 */
export function useUpsertLead() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (lead: {
      id?: string
      list_id: number
      phone_number: string
      status: string
      data?: Record<string, string>
    }) => {
      return apiClient<{ message: string; action: string }>('/leads', {
        method: 'PUT',
        body: JSON.stringify(lead),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}